import { useState } from "react"
import { useMutation } from "@tanstack/react-query"

import { promocodesApi, type ApiPostPromocodeActivate } from "@/shared/api"
import { useI18n } from "@/shared/i18n"
import { makeErrorToast } from "@/shared/lib/toast-error"
import { AnalyticsEvent, trackAnalyticsEvent } from "@/shared/lib/analytics"

import {
  buildClaimedReward,
  localizeErrorCode,
  readApiErrorCode,
  type ClaimedPromoReward,
} from "./usePromocodeActivation.utils"

export type { ClaimedPromoReward }

export interface UsePromocodeActivationResult {
  activate: (code: string) => Promise<ApiPostPromocodeActivate.Response>
  isPending: boolean
  claimedReward: ClaimedPromoReward | null
  dismissReward: () => void
}

export function usePromocodeActivation(): UsePromocodeActivationResult {
  const { t } = useI18n()
  const [claimedReward, setClaimedReward] = useState<ClaimedPromoReward | null>(
    null
  )

  const mutation = useMutation({
    mutationFn: ({ code }: { code: string }) =>
      promocodesApi.activate({ code }),
    onSuccess: (result, { code }) => {
      trackAnalyticsEvent(AnalyticsEvent.PromoCodeSubmitted, {
        result: "success",
        code_length: code.length,
      })
      const reward = buildClaimedReward(result, t)
      if (reward) setClaimedReward(reward)
      else makeErrorToast({ label: t("rewards.claimError") })
    },
    onError: (error, { code }) => {
      trackAnalyticsEvent(AnalyticsEvent.PromoCodeSubmitted, {
        result: "failed",
        code_length: code.length,
      })
      makeErrorToast({ label: localizeErrorCode(readApiErrorCode(error), t) })
    },
  })

  return {
    activate: (code) => {
      const trimmed = code.trim()
      if (trimmed.length === 0) {
        return Promise.reject(new Error("Empty promocode"))
      }
      return mutation.mutateAsync({ code: trimmed })
    },
    isPending: mutation.isPending,
    claimedReward,
    dismissReward: () => setClaimedReward(null),
  }
}
